import React from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Slide, CarouselStyle, LayoutSettings } from '@/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown, Upload, Sparkles, Pencil, X, Loader2 } from 'lucide-react';

interface ImagePanelProps {
  activeSlide: Slide;
  activeIndex: number;
  slides: Slide[];
  style: CarouselStyle;
  layoutSettings: LayoutSettings;
  generatingSlideIds: Set<string>;
  imageSettingsOpen: boolean;
  setImageSettingsOpen: (open: boolean) => void;
  onUpdateSlides: (slides: Slide[]) => void;
  onToggleImage: (checked: boolean) => void;
  onToggleOverlay: (checked: boolean) => void;
  onGenerateImage: () => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenEditModal: () => void;
  onImageScaleChange: (scale: number) => void;
  onImageOffsetChange: (offset: number) => void;
  onGradientHeightChange: (height: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

const ImagePanel: React.FC<ImagePanelProps> = ({
  activeSlide,
  activeIndex,
  slides,
  style,
  layoutSettings,
  generatingSlideIds,
  imageSettingsOpen,
  setImageSettingsOpen,
  onUpdateSlides,
  onToggleImage,
  onToggleOverlay,
  onGenerateImage,
  onFileUpload,
  onOpenEditModal,
  onImageScaleChange,
  onImageOffsetChange,
  onGradientHeightChange,
  fileInputRef,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-3 space-y-4">
      <div className="border border-border rounded-lg">
        <div
          className="flex items-center justify-between p-3 bg-secondary/50 cursor-pointer hover:bg-secondary/80 transition-colors rounded-t-lg"
          onClick={() => activeSlide.showImage && setImageSettingsOpen(!imageSettingsOpen)}
        >
          <div className="flex items-center gap-2">
            {activeSlide.showImage && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setImageSettingsOpen(!imageSettingsOpen); }} className="text-muted-foreground hover:text-foreground">
                {imageSettingsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            )}
            <span className="font-semibold text-sm">{t('workspace.illustrationImage')}</span>
          </div>
          <Switch
            checked={activeSlide.showImage}
            onCheckedChange={(checked) => { onToggleImage(checked); if (checked) setImageSettingsOpen(true); }}
          />
        </div>

        {activeSlide.showImage && imageSettingsOpen && (
          <div className="p-3 border-t border-border animate-fade-in">
            {/* Overlay Toggle (Storyteller Only) */}
            {style === CarouselStyle.STORYTELLER && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                <span className="text-xs text-muted-foreground">{t('workspace.topFadeOverlay')}</span>
                <Switch checked={activeSlide.overlayImage !== false} onCheckedChange={(checked) => onToggleOverlay(checked)} />
              </div>
            )}

            {/* Content Layout (Twitter Only) */}
            {style === CarouselStyle.TWITTER && (
              <div className="mb-4 pb-4 border-b border-border">
                <span className="text-xs text-muted-foreground block mb-2">{t('workspace.imagePosition')}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'default' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", (!activeSlide.contentLayout || activeSlide.contentLayout === 'default') ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionBelow')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'image-after-title' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", activeSlide.contentLayout === 'image-after-title' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionAfterTitle')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'image-first' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", activeSlide.contentLayout === 'image-first' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionTop')}</Button>
                </div>
                {activeSlide.contentLayout === 'image-after-title' && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex justify-between text-[10px] text-muted-foreground mb-1"><span>{t('workspace.imageTextSpacing')}</span><span>{activeSlide.imageTextSpacing ?? 16}px</span></div>
                    <Slider value={[activeSlide.imageTextSpacing ?? 16]} min={0} max={64} step={4} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageTextSpacing: value[0] }; onUpdateSlides(newSlides); }} />
                  </div>
                )}
              </div>
            )}

            {/* Image Preview / Actions */}
            {activeSlide.imageUrl ? (
              <div className="relative aspect-square rounded-md overflow-hidden mb-3 border border-border">
                <img src={activeSlide.imageUrl} alt={t('workspace.thumbnailAlt')} className="w-full h-full object-cover" />
                <div className="absolute top-1 right-1 flex gap-1">
                  <Button variant="ghost" size="icon" onClick={onOpenEditModal} className="bg-black/50 hover:bg-primary text-white h-6 w-6" title={t('workspace.editWithAI')}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageUrl: undefined }; onUpdateSlides(newSlides); }} className="bg-black/50 hover:bg-destructive text-white h-6 w-6" title={t('workspace.removeImage')}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ) : (
              <div className="h-32 bg-secondary rounded-md border-2 border-dashed border-border flex items-center justify-center mb-3">
                <span className="text-muted-foreground text-xs">{t('workspace.noImageSelected')}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mb-4">
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()} className="text-xs"><Upload className="h-4 w-4 mr-2" />{t('workspace.uploadFile')}</Button>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={onFileUpload} />
              <Button onClick={onGenerateImage} disabled={generatingSlideIds.has(activeSlide.id)} className="text-xs">
                {generatingSlideIds.has(activeSlide.id) ? (<><Loader2 className="animate-spin h-4 w-4 mr-2" />{t('workspace.generating')}</>) : (<><Sparkles className="h-4 w-4 mr-2" />{t('workspace.aiGenerate')}</>)}
              </Button>
            </div>

            {/* Image Controls */}
            {activeSlide.imageUrl && (
              <div className="space-y-4 pt-2 border-t border-border">
                {(style === CarouselStyle.STORYTELLER || !activeSlide.overlayImage) && (
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageHeight')}</span><span>{activeSlide.imageScale || 50}%</span></div>
                    <Slider value={[activeSlide.imageScale || 50]} min={10} max={90} step={1} onValueChange={(value) => onImageScaleChange(value[0])} />
                  </div>
                )}
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.verticalPosition')}</span><span>{activeSlide.imageOffsetY !== undefined ? activeSlide.imageOffsetY : 50}%</span></div>
                  <Slider value={[activeSlide.imageOffsetY !== undefined ? activeSlide.imageOffsetY : 50]} min={0} max={100} step={1} onValueChange={(value) => onImageOffsetChange(value[0])} />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{t('workspace.positionScaleTop')}</span><span>{t('workspace.positionScaleCenter')}</span><span>{t('workspace.positionScaleBottom')}</span></div>
                </div>
                {(style === CarouselStyle.STORYTELLER && activeSlide.overlayImage !== false) && (
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.overlayFadeHeight')}</span><span>{activeSlide.gradientHeight !== undefined ? activeSlide.gradientHeight : 60}%</span></div>
                    <Slider value={[activeSlide.gradientHeight !== undefined ? activeSlide.gradientHeight : 60]} min={0} max={100} step={1} onValueChange={(value) => onGradientHeightChange(value[0])} />
                  </div>
                )}
                {style === CarouselStyle.TWITTER && (
                  <>
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageZoom')}</span><span>{Math.round((activeSlide.imageZoom ?? layoutSettings.imageZoom) * 100)}%</span></div>
                      <Slider value={[activeSlide.imageZoom ?? layoutSettings.imageZoom]} min={0.5} max={2.0} step={0.1} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageZoom: value[0] }; onUpdateSlides(newSlides); }} />
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{t('workspace.zoomOut')}</span><span>100%</span><span>{t('workspace.zoomIn')}</span></div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageMargin')}</span><span>{activeSlide.imageMargin ?? layoutSettings.imageMargin}px</span></div>
                      <Slider value={[activeSlide.imageMargin ?? layoutSettings.imageMargin]} min={0} max={32} step={4} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageMargin: value[0] }; onUpdateSlides(newSlides); }} />
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-3">
              <Label className="text-xs text-muted-foreground block mb-1">{t('workspace.promptOverride')}</Label>
              <Textarea className="w-full h-16 text-xs resize-none" placeholder={t('workspace.describeImagePlaceholder')} value={activeSlide.imagePrompt || ''} onChange={(e) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imagePrompt: e.target.value }; onUpdateSlides(newSlides); }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagePanel;
